import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import RijekaButton from './RijekaButton';
import ImageLightbox from './ImageLightbox';
import RoomRatesAccordion from './RoomRatesAccordion';
import PricingPolicyModal from './PricingPolicyModal';
import { FileText } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';

// CSS to force full height on carousel
const carouselStyles = `
  .room-carousel,
  .room-carousel > div,
  .room-carousel .overflow-hidden,
  .room-carousel .flex {
    height: 100% !important;
  }
`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoomCard = ({ room, property, onSelect, checkIn, checkOut, adults, children, onDatesChange, showMinStayWarning = false }) => {
  const { t, i18n } = useTranslation();
  const [roomMedia, setRoomMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [ratesExpanded, setRatesExpanded] = useState(false);
  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [pricingPolicyOpen, setPricingPolicyOpen] = useState(false);

  useEffect(() => {
    fetchRoomMedia();
    fetchRates();
  }, [room.roomId, i18n.language]);

  // Calculate number of nights
  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const nights = calculateNights();
  // Use base_price for calculations, extra_guest_total is added to get full price
  const basePriceTotal = room.base_price || room.price;
  const basePricePerNight = nights > 0 ? basePriceTotal / nights : basePriceTotal;
  const extraGuestTotal = room.extra_guest_total || 0;
  const extraGuestPerNight = nights > 0 ? extraGuestTotal / nights : 0;

  // MinStay info
  const minStayInfo = room.min_stay_info || {};
  const hasMinStayIssue = room.min_stay_required && showMinStayWarning;
  
  // Format date for display (YYYY-MM-DD to DD.MM.YYYY)
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  };
  
  // Get day name in Croatian
  const getDayName = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const days = ['Nedjelja', 'Ponedjeljak', 'Utorak', 'Srijeda', 'Četvrtak', 'Petak', 'Subota'];
    return days[date.getDay()];
  };
  
  // Handle adjust dates for minStay
  const handleAdjustDates = () => {
    if (minStayInfo.suggested_checkout && onDatesChange) {
      onDatesChange(checkIn, minStayInfo.suggested_checkout, room.roomId);
    }
  };
  
  // Listen for expand event from PropertiesPage
  useEffect(() => {
    const handleExpandEvent = (event) => {
      if (event.detail.roomId === room.roomId) {
        setRatesExpanded(true);
      }
    };
    window.addEventListener('expandRoomRates', handleExpandEvent);
    return () => window.removeEventListener('expandRoomRates', handleExpandEvent);
  }, [room.roomId]);

  const fetchRates = async () => {
    try {
      const response = await axios.get(`${API}/rates/room/${room.roomId}`, {
        params: { check_in: checkIn, check_out: checkOut }
      });
      setRates(response.data);
    } catch (error) {
      console.error('Error fetching rates:', error);
      setRates([]);
    } finally {
      setRatesLoading(false);
    }
  };

  // Calculate best discounted price
  // Discount applies to TOTAL price (base + extra guests)
  // Formula: (base + extra_guests) * (1 - discount%)
  const getBestPricePerNight = () => {
    // Total price per night before discount (base + extra guests)
    const totalPricePerNightBeforeDiscount = basePricePerNight + extraGuestPerNight;
    
    if (!rates || rates.length === 0) {
      // No discount - return total price (base + extra guests)
      return { pricePerNight: totalPricePerNightBeforeDiscount, hasDiscount: false, discount: 0, basePricePerNight };
    }
    
    // Find the best discount rate that user qualifies for
    let bestDiscount = 0;
    rates.forEach(rate => {
      const minNights = rate.min_nights || 1;
      const maxNights = rate.max_nights || Infinity;
      if (nights >= minNights && nights <= maxNights) {
        if (rate.discount_percent > bestDiscount) {
          bestDiscount = rate.discount_percent;
        }
      }
    });
    
    if (bestDiscount > 0) {
      // Apply discount to TOTAL price (base + extra guests)
      const discountedTotalPricePerNight = totalPricePerNightBeforeDiscount * (1 - bestDiscount / 100);
      return { pricePerNight: discountedTotalPricePerNight, hasDiscount: true, discount: bestDiscount, basePricePerNight };
    }
    
    return { pricePerNight: totalPricePerNightBeforeDiscount, hasDiscount: false, discount: 0, basePricePerNight };
  };

  const bestPrice = getBestPricePerNight();

  const fetchRoomMedia = async () => {
    try {
      const response = await axios.get(`${API}/rooms/${room.roomId}/media?lang=${i18n.language}`);
      setRoomMedia(response.data);
    } catch (error) {
      console.error('Error fetching room media:', error);
      setRoomMedia({ images: [], description: null, room_name: null });
    } finally {
      setLoading(false);
    }
  };

  const hasImages = roomMedia?.images && roomMedia.images.length > 0;
  const displayName = roomMedia?.room_name || room.name || 'Room';
  const displayDescription = roomMedia?.description || room.description;

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % roomMedia.images.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + roomMedia.images.length) % roomMedia.images.length);
  };

  return (
    <>
      <style>{carouselStyles}</style>
      <Card 
        className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white border-2 border-transparent hover:border-[var(--color-primary)]"
        data-room-id={room.roomId}
      >
        {/* NEW LAYOUT: Gallery on top, content below */}
        <div className="flex flex-col">
          
          {/* Image Gallery Section - Full width on top */}
          <div className="relative bg-[var(--color-border)] w-full" style={{height: '280px'}}>
            {loading ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="animate-spin h-12 w-12 border-4 border-[var(--color-primary)] border-r-transparent rounded-full"></div>
              </div>
            ) : hasImages ? (
              <>
                {/* Mobile Carousel - Single images */}
                <div className="md:hidden h-full">
                  <Carousel className="w-full h-full room-carousel" style={{height: '100%'}}>
                    <CarouselContent className="h-full" style={{height: '100%', marginLeft: 0}}>
                      {roomMedia.images.map((imageUrl, index) => (
                        <CarouselItem key={index} className="h-full pl-0" style={{height: '100%', paddingLeft: 0}}>
                          <img
                            src={imageUrl}
                            alt={`${displayName} - Image ${index + 1}`}
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            style={{width: '100%', height: '100%', objectFit: 'cover'}}
                            onClick={() => openLightbox(index)}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%23D3D0C7"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2335393C" font-size="20">Image Not Available</text></svg>';
                            }}
                          />
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {roomMedia.images.length > 1 && (
                      <>
                        <CarouselPrevious className="left-4 bg-white/80 hover:bg-white text-[var(--color-text-primary)] border-0 shadow-md" />
                        <CarouselNext className="right-4 bg-white/80 hover:bg-white text-[var(--color-text-primary)] border-0 shadow-md" />
                      </>
                    )}
                  </Carousel>
                </div>
                
                {/* Desktop Carousel - Shows two images, swipes one at a time */}
                <div className="hidden md:block h-full">
                  <Carousel 
                    className="w-full h-full room-carousel" 
                    style={{height: '100%'}}
                    opts={{
                      align: 'start',
                      slidesToScroll: 1
                    }}
                  >
                    <CarouselContent className="h-full" style={{height: '100%', marginLeft: 0}}>
                      {roomMedia.images.map((imageUrl, index) => (
                        <CarouselItem 
                          key={index} 
                          className="h-full pl-0 basis-1/2" 
                          style={{height: '100%', paddingLeft: index === 0 ? 0 : '2px'}}
                        >
                          <img
                            src={imageUrl}
                            alt={`${displayName} - Image ${index + 1}`}
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            style={{width: '100%', height: '100%', objectFit: 'cover'}}
                            onClick={() => openLightbox(index)}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%23D3D0C7"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2335393C" font-size="20">Image Not Available</text></svg>';
                            }}
                          />
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {roomMedia.images.length > 2 && (
                      <>
                        <CarouselPrevious className="left-4 bg-white/80 hover:bg-white text-[var(--color-text-primary)] border-0 shadow-md" />
                        <CarouselNext className="right-4 bg-white/80 hover:bg-white text-[var(--color-text-primary)] border-0 shadow-md" />
                      </>
                    )}
                  </Carousel>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[var(--color-border)]">
                <div className="text-center text-[var(--color-text-primary)]">
                  <svg className="w-20 h-20 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm font-medium">No images available</p>
                </div>
              </div>
            )}
          </div>

          {/* Room Information Section - Below gallery */}
          <div className="p-5 lg:p-6">
            {/* Room Title */}
            <h3 className="text-2xl font-bold mb-2 text-[var(--color-text-primary)]">
              {displayName}
            </h3>

            {/* Property Name */}
            <p className="text-xs text-gray-500 mb-4 uppercase tracking-wide font-semibold">
              {property.name || property.property_name}
            </p>

            {/* Description */}
            {displayDescription && (
              <p className="text-gray-700 mb-5 leading-relaxed text-sm">
                {displayDescription}
              </p>
            )}

            {/* Room Features & Amenities */}
            <div className="space-y-4 mb-5">
              {/* Max Guests + Bed Info Row */}
              <div className="flex items-center gap-4 text-sm text-[var(--color-text-primary)] flex-wrap">
                {/* Max Guests */}
                {room.maxPeople && (
                  <div className="flex items-center gap-2 font-medium">
                    {roomMedia?.max_guests_icon_url ? (
                      <img src={roomMedia.max_guests_icon_url} alt="" className="w-10 h-10 object-contain" />
                    ) : (
                      <svg className="w-5 h-5 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )}
                    <span>{t('booking.maxGuests', 'Maks. Gosti')}: {room.maxPeople}</span>
                  </div>
                )}
                
                {/* Bed Info with Custom Icons */}
                {(() => {
                  const langKey = `bed_info_${i18n.language}`;
                  const bedInfo = roomMedia?.[langKey] || roomMedia?.bed_info_hr || [];
                  
                  return bedInfo.map((info, idx) => (
                    <div key={idx} className="flex items-center gap-2 font-medium">
                      {info.icon_url ? (
                        <img src={info.icon_url} alt="" className="w-10 h-10 object-contain" />
                      ) : (
                        <svg className="w-5 h-5 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      )}
                      <span>{info.text}</span>
                    </div>
                  ));
                })()}
              </div>
              
              {/* Featured Amenities with Icons - Inline layout matching row above */}
              {(() => {
                const langKey = `amenities_with_icons_${i18n.language}`;
                const amenitiesWithIcons = roomMedia?.[langKey] || roomMedia?.amenities_with_icons_hr || [];
                const featuredAmenities = amenitiesWithIcons.filter(a => a.is_featured);
                
                if (featuredAmenities.length > 0) {
                  return (
                    <div className="space-y-2 pt-3 border-t border-[var(--color-border)]">
                      <p className="text-xs font-semibold text-[var(--color-text-primary)] uppercase tracking-wide">{t('properties.amenities')}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {featuredAmenities.slice(0, 4).map((amenity, index) => (
                          <div 
                            key={index}
                            className="flex items-center gap-2 text-sm text-[var(--color-text-primary)] font-medium"
                          >
                            {amenity.icon_url ? (
                              <img src={amenity.icon_url} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                            ) : (
                              <svg className="w-5 h-5 flex-shrink-0 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            <span>{amenity.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                
                // Fallback to old amenities if no featured icons
                if (roomMedia?.amenities && roomMedia.amenities.length > 0) {
                  return (
                    <div className="space-y-2 pt-3 border-t border-[var(--color-border)]">
                      <p className="text-xs font-semibold text-[var(--color-text-primary)] uppercase tracking-wide">{t('properties.amenities')}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {roomMedia.amenities.slice(0, 4).map((amenity, index) => (
                          <div 
                            key={index}
                            className="flex items-center gap-2 text-sm text-[var(--color-text-primary)] font-medium"
                          >
                            <svg className="w-5 h-5 flex-shrink-0 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{amenity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                
                return null;
              })()}
            </div>

            {/* MinStay Warning Box - only shown when minStay not satisfied */}
            {hasMinStayIssue && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800 mb-1">
                      {t('properties.minStayRequired', 'Potrebno minimum {{nights}} noći').replace('{{nights}}', minStayInfo.required_nights)}
                    </p>
                    <p className="text-xs text-amber-700 mb-3">
                      {minStayInfo.min_stay_date && (
                        <>
                          {getDayName(minStayInfo.min_stay_date)} ({formatDateDisplay(minStayInfo.min_stay_date)}) {t('properties.requiresLongerStay', 'zahtijeva dulji boravak')}.
                        </>
                      )}
                      {' '}{t('properties.yourSelection', 'Vaš odabir')}: {minStayInfo.your_nights} {minStayInfo.your_nights === 1 ? t('properties.night') : t('properties.nights')}.
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <div className="text-sm">
                        <span className="text-amber-800 font-medium">{t('properties.suggestedDates', 'Predloženi termin')}:</span>
                        <span className="ml-2 text-amber-900 font-semibold">
                          {formatDateDisplay(checkIn)} → {formatDateDisplay(minStayInfo.suggested_checkout)}
                        </span>
                        <span className="ml-2 text-amber-700">
                          ({minStayInfo.required_nights} {minStayInfo.required_nights === 1 ? t('properties.night') : t('properties.nights')})
                        </span>
                      </div>
                      <button
                        onClick={handleAdjustDates}
                        className="px-4 py-2 text-sm font-semibold bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors whitespace-nowrap"
                        data-testid={`adjust-dates-btn-${room.roomId}`}
                      >
                        {t('properties.adjustDates', 'Prilagodi datume')}
                      </button>
                    </div>
                    {minStayInfo.suggested_price && (
                      <p className="text-xs text-amber-600 mt-2">
                        {t('properties.newPrice', 'Nova cijena')}: €{Math.round(minStayInfo.suggested_price)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Price and View Prices Button */}
            <div className="pt-4 border-t-2 border-[var(--color-border)]">
              <div className="flex flex-col min-[500px]:flex-row min-[500px]:items-end min-[500px]:justify-between gap-3">
                <div>
                  <div className="text-2xl font-bold mb-1 text-[var(--color-text-primary)]">
                    {t('properties.from')} €{nights > 0 ? Math.round(bestPrice.pricePerNight * nights) : Math.round(bestPrice.pricePerNight)}
                  </div>
                  <div className="text-xs text-gray-600 font-medium">
                    {nights > 0 ? (
                      <>
                        <span className="text-[var(--color-primary)] font-semibold">
                          €{Math.round(bestPrice.pricePerNight)}
                        </span> {t('properties.perNight')} • {nights} {nights === 1 ? t('properties.night') : t('properties.nights')}
                      </>
                    ) : (
                      t('booking.totalPrice')
                    )}
                  </div>
                  {/* Extra guest info - clarify that price includes extra guests */}
                  {room.extra_guest_price > 0 && room.extra_guests_count > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      {t('properties.includesExtraGuestsShort', 'Uklj. {{count}} dod. gosta').replace('{{count}}', room.extra_guests_count)}
                    </div>
                  )}
                  {/* Pricing Policy Link */}
                  <button
                    onClick={() => setPricingPolicyOpen(true)}
                    className="text-xs text-[var(--color-primary)] hover:underline mt-2 flex items-center gap-1"
                    data-testid={`pricing-policy-link-${room.roomId}`}
                  >
                    <FileText className="w-3 h-3" />
                    {t('properties.pricingPolicy')}
                  </button>
                </div>
                <button
                  onClick={() => setRatesExpanded(!ratesExpanded)}
                  className="px-8 py-3 text-sm font-bold w-full min-[500px]:w-auto border-2 border-[var(--color-primary)] text-[var(--color-primary)] bg-transparent hover:bg-[var(--color-primary)]/10 transition-colors rounded-md uppercase tracking-wide"
                  data-testid={`view-prices-btn-${room.roomId}`}
                >
                  {t('properties.viewPrices')}
                </button>
              </div>
            </div>
          </div>
        </div>
      
        {/* Rates Accordion - directly controlled, no toggle button inside */}
        {ratesExpanded && (
          <div className="px-4 pb-4">
            <RoomRatesAccordion
              room={room}
              property={property}
              checkIn={checkIn}
              checkOut={checkOut}
              adults={adults || 1}
              children={children || 0}
              isExpanded={ratesExpanded}
              onToggle={() => setRatesExpanded(!ratesExpanded)}
              onDatesChange={onDatesChange}
            />
          </div>
        )}
      </Card>
    
    {/* Image Lightbox */}
    {lightboxOpen && hasImages && (
      <ImageLightbox
        images={roomMedia.images}
        currentIndex={lightboxIndex}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrev={prevImage}
      />
    )}
    
    {/* Pricing Policy Modal */}
    <PricingPolicyModal
      isOpen={pricingPolicyOpen}
      onClose={() => setPricingPolicyOpen(false)}
      policyText={property.pricing_policy}
      extraGuestPrice={roomMedia?.extra_guest_price || 0}
      roomName={room.name}
    />
    </>
  );
};

export default RoomCard;
